/**
 * SectionsListManager — Reusable "Add + sortable list + delete confirmation"
 * manager for LayoutSection arrays.
 *
 * Used by:
 *   - V2FlexibleSectionsSection (sortable + delete confirmation)
 *   - EmbedChildSectionsManager (simple list, instant delete)
 *   - Future datasource-embedded section editors
 *
 * Encapsulates:
 *   - Header row: "TITLE (N)" + Add button
 *   - Dropdown menu with configurable options
 *   - Optional DnD reordering (@dnd-kit)
 *   - Optional delete confirmation dialog (SDialog)
 *   - Row rendering via `renderRow` slot prop
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { PaperProps } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { SDialog, WithGuideHelp } from '@modules/ui-components';
import type { LayoutSection } from '@segmently/schema-models/v2';
import { createDefaultSection, type AddMenuKind } from '@modules/reactFlow/config/flexibleSectionDefaults';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { useEditorGuideService } from '@modules/reactFlow/contexts';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SectionMenuOption {
  value: AddMenuKind;
  label: string;
}

export interface SectionsListManagerProps {
  /** Current sections array (should be sorted by .order) */
  sections: LayoutSection[];
  /** Write updated sections back to the parent */
  writeSections: (updated: LayoutSection[]) => void;
  /** Current locale — passed to createDefaultSection */
  locale: string;
  /** Available options for the Add dropdown menu */
  menuOptions: SectionMenuOption[];
  /** Header label, e.g. "SECTIONS" or "DATA SOURCES" */
  title: string;
  /** Optional label inside the dropdown menu header */
  menuTitle?: string;
  /** Enable DnD reordering (default: false) */
  sortable?: boolean;
  /** Enable delete confirmation dialog (default: false — instant delete) */
  confirmDelete?: boolean;
  /** Render a single section row. Receives section + onDelete callback. */
  renderRow: (
    section: LayoutSection,
    onDelete: (sectionId: string, label: string) => void,
  ) => React.ReactNode;
  /** Disables Add button */
  disabled?: boolean;
  /** Optional message shown when sections array is empty */
  emptyMessage?: string;
  /** Hide header row (title + count). Parent manages its own title. (default: false) */
  hideHeader?: boolean;
  /** Theme settings for themed defaults when creating new sections */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
  /** Called after a section is deleted — allows parent to clean up related data */
  onSectionDeleted?: (deletedSection: LayoutSection) => void;
  /** Called after a section is added — allows parent to initialize related data */
  onSectionAdded?: (addedSection: LayoutSection) => void;
  /** Optional guide key for the Add button. Used by FlexibleLayout section management. */
  addGuideKey?: string;
  /** Article documents only: the article's defaultLanguage (keys the default ArticleIndex heading, R-32) */
  articleDefaultLanguage?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const SectionsListManager: React.FC<SectionsListManagerProps> = ({
  sections,
  writeSections,
  locale,
  menuOptions,
  title,
  menuTitle,
  sortable = false,
  confirmDelete = false,
  renderRow,
  disabled = false,
  emptyMessage,
  hideHeader = false,
  themeSettingsV2,
  onSectionDeleted,
  onSectionAdded,
  addGuideKey,
  articleDefaultLanguage,
}) => {
  const guideService = useEditorGuideService();
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    sectionId: string;
    label: string;
  }>({ open: false, sectionId: '', label: '' });

  // ── Add section ──────────────────────────────────────────────────────

  const handleAdd = useCallback(
    (menuKind: AddMenuKind) => {
      setAddMenuAnchor(null);
      const newSection = createDefaultSection(menuKind, sections.length, locale, themeSettingsV2, { articleDefaultLanguage });
      writeSections([...sections, newSection]);
      onSectionAdded?.(newSection);
    },
    [sections, writeSections, locale, themeSettingsV2, onSectionAdded, articleDefaultLanguage],
  );

  // ── Delete section ───────────────────────────────────────────────────

  const handleDeleteRequest = useCallback(
    (sectionId: string, label: string) => {
      if (confirmDelete) {
        setDeleteState({ open: true, sectionId, label });
      } else {
        const deleted = sections.find((s) => s.id === sectionId);
        const filtered = sections.filter((s) => s.id !== sectionId);
        const cleaned = filtered.map((s) =>
          s.parentSectionId === sectionId ? { ...s, parentSectionId: undefined } : s,
        );
        writeSections(cleaned.map((s, i) => ({ ...s, order: i })));
        if (deleted) onSectionDeleted?.(deleted);
      }
    },
    [confirmDelete, sections, writeSections, onSectionDeleted],
  );

  const handleDeleteConfirm = useCallback(() => {
    const deleted = sections.find((s) => s.id === deleteState.sectionId);
    const filtered = sections.filter((s) => s.id !== deleteState.sectionId);
    const cleaned = filtered.map((s) =>
      s.parentSectionId === deleteState.sectionId ? { ...s, parentSectionId: undefined } : s,
    );
    writeSections(cleaned.map((s, i) => ({ ...s, order: i })));
    setDeleteState({ open: false, sectionId: '', label: '' });
    if (deleted) onSectionDeleted?.(deleted);
  }, [deleteState.sectionId, sections, writeSections, onSectionDeleted]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteState({ open: false, sectionId: '', label: '' });
  }, []);

  // ── DnD (only when sortable) ────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(sections, oldIndex, newIndex).map((s, i) => ({
        ...s,
        order: i,
      }));
      writeSections(reordered);
    },
    [sections, writeSections],
  );

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  // ── Render rows (with or without DnD) ───────────────────────────────

  const rowElements = sections.map((s) => renderRow(s, handleDeleteRequest));

  const listContent = sortable ? (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
        {rowElements}
      </SortableContext>
    </DndContext>
  ) : (
    rowElements
  );

  const renderAddButton = (compact: boolean) => {
    const button = (
      <Button
        data-testid="sections-add-btn"
        startIcon={<AddIcon />}
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        sx={compact ? {
          position: 'relative',
          overflow: 'hidden',
          '@keyframes shimmer': {
            '0%': { left: '-100%' },
            '100%': { left: '100%' },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
        } : undefined}
      >
        Add
      </Button>
    );

    if (!addGuideKey || !guideService) return button;

    return (
      <WithGuideHelp
        guideKey={addGuideKey}
        guideService={guideService}
        data-testid={`guide-anchor-${addGuideKey}`}
        position="top-left"
        iconSize="small"
        stopClickPropagation
      >
        {button}
      </WithGuideHelp>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <>
      {/* Header: "TITLE (N)" + Add button — hidden when parent manages its own title */}
      {!hideHeader && (
        <Box sx={{ px: 0.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {title} ({sections.length})
          </Typography>
          {renderAddButton(false)}
        </Box>
      )}

      {/* Compact Add button when header is hidden */}
      {hideHeader && (
        <Box sx={{ px: 0.5, pb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
          {renderAddButton(true)}
        </Box>
      )}

      {/* Section rows */}
      <Box sx={{ px: 0 }}>
        {listContent}

        {sections.length === 0 && emptyMessage && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Add section dropdown menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        slotProps={{ paper: { 'data-testid': 'sections-add-menu' } as PaperProps }}
      >
        {menuTitle && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
            {menuTitle}
          </Typography>
        )}
        {menuTitle && <Divider />}
        {menuOptions.map((opt) => (
          <MenuItem key={opt.value} data-testid={`sections-menu-${opt.value}`} onClick={() => handleAdd(opt.value)} dense>
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Delete confirmation dialog (only rendered when confirmDelete is enabled) */}
      {confirmDelete && (
        <SDialog
          data-testid="section-delete-confirm-dialog"
          open={deleteState.open}
          onClose={closeDeleteDialog}
          title="Delete Section?"
          // Destructive confirmation.
          showCloseButton={false}
          primaryAction={{
            label: 'Delete',
            onClick: handleDeleteConfirm,
            color: 'error',
            variant: 'contained',
          }}
          secondaryAction={{
            label: 'Cancel',
            onClick: closeDeleteDialog,
            variant: 'outlined',
          }}
        >
          <Typography>
            Are you sure you want to delete &quot;{deleteState.label}&quot;?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </SDialog>
      )}
    </>
  );
};
